<template>
    <section
        ref="section"
        :class="['slice slice-hero slice-hero--with-background', {'slice-hero--hide-in-mobile': slice.primary.hideInMobile}]"
        aria-label="hero"
    >
        <div :class="['slice-hero--with-background__polygon', {'slice-hero--with-background__polygon--left-bottom': hasLeftShapePosition}]"></div>
        <div class="slice-content">
            <MktTitleBrand :field="slice.primary.title" class="slice-hero--with-background__title" data-testid="slice-hero-title" />
            <div v-if="slice.primary.subtitle" class="slice-hero--with-background__subtitle" data-testid="slice-hero-subtitle">
                {{ slice.primary.subtitle }}
            </div>
            <div
                v-if="Array.isArray(slice.primary.description) && isFilled.richText(slice.primary.description)"
                class="slice-hero--with-background__description"
                data-testid="slice-hero-description"
            >
                <PrismicRichText :field="slice.primary.description" />
            </div>
            <div v-if="buttons.length" class="slice-hero--with-background__ctas">
                <div v-for="(button, idx) in buttons" :key="idx" :class="`slice-hero--with-background__cta-wrapper--${button.variant}`">
                    <VJoyButton :href="$withLocalePrefix(button.url)" :variant="button.variant" data-component-source="hero">
                        {{ button.text }}
                    </VJoyButton>
                </div>
            </div>
        </div>
    </section>
</template>
<script setup lang="ts">
    import {computed} from 'vue';
    import {isFilled} from '@prismicio/helpers';
    import {getSliceComponentProps, PrismicRichText} from '@prismicio/vue';
    import type {HeroSlice, HeroSliceHeroWithBackground} from '@prismicio-types';
    import {MktTitleBrand} from '#components';
    import {VJoyButton} from '@maltjoy/core-vue';

    const props = defineProps(getSliceComponentProps<HeroSlice>());
    const slice = computed(() => props.slice as HeroSliceHeroWithBackground);

    type CtaButton = {url: string; text: string; variant: 'primary' | 'secondary' | 'main' | undefined};

    const hasLeftShapePosition = computed(() => slice.value.primary.shapesPosition === 'left');

    const buttons = computed<CtaButton[]>(() =>
        (slice.value.primary.buttons ?? [])
            .filter((button) => isFilled.link(button.cta))
            .map((button) => {
                const cta = button.cta as {url: string; text?: string; variant?: 'primary' | 'secondary' | 'main'};
                return {url: cta.url, text: cta.text ?? '', variant: cta.variant};
            }),
    );
</script>
<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    .slice-hero {
        &--with-background {
            --hero-header-height: 590px;
            --hero-header-nav-height: 72px;
            --hero-polygon-height-variation: 25px;
            --hero-polygon-background-size: auto 250px;
            --hero-background-position: 100% calc(100% + var(--joy-core-spacing-5));
            --hero-title-font-size: 48px;
            --hero-subtitle-font-size: 24px;

            min-height: var(--hero-header-height);
            height: max-content;
            display: flex;
            align-items: center;

            &__title.title-brand > * {
                font-size: var(--hero-title-font-size);
                line-height: var(--joy-line-height-small);
                color: var(--joy-color-neutral-0);
            }

            &__subtitle {
                font-size: var(--hero-subtitle-font-size);
                font-family: var(--joy-font-family-title);
                margin-bottom: var(--joy-core-spacing-2);
                line-height: var(--joy-line-height-small);
                color: var(--joy-color-neutral-0);
            }

            &__description {
                color: var(--joy-color-neutral-0);
                * {
                    color: inherit;
                }
                a {
                    text-decoration: underline;
                }
            }

            &__polygon {
                position: absolute;
                width: 100%;
                height: 100%;
                background-color: var(--joy-color-primary-50);
                background-position: var(--hero-background-position);
                background-repeat: no-repeat;
                background-size: var(--hero-polygon-background-size);
                padding-bottom: 100px;
                z-index: -1;
                clip-path: polygon(100% 0%, 100% calc(100% - var(--hero-polygon-height-variation)), 0% 100%, 0 0);
                background-image: url('../../../../assets/svg/hero-default-right-shapes.svg');

                &--left-bottom {
                    clip-path: polygon(0% 0%, 100% 0%, 100% 100%, 0% calc(100% - var(--hero-polygon-height-variation)));
                    background-image: url('../../../../assets/svg/hero-default-left-shapes.svg');
                    background-position: left bottom;
                    background-size: 100% auto;
                }
            }

            &__ctas {
                display: flex;
                flex-direction: column;
                justify-content: center;
                gap: var(--joy-core-spacing-5);
                padding-top: 50px;

                @include mq.screen_xs(min) {
                    flex-direction: row;
                    padding-bottom: 90px;
                }

                .joy-button {
                    width: 100%;

                    @include mq.screen_sm(min) {
                        width: auto;
                    }
                }
            }

            & &__cta-wrapper--primary {
                .joy-button {
                    color: var(--joy-color-primary-50);
                    background-color: var(--joy-color-neutral-0);

                    &:hover {
                        background-color: var(--joy-color-neutral-20);
                    }
                }
            }

            & &__cta-wrapper--secondary {
                .joy-button {
                    color: var(--joy-color-neutral-0);
                    background-color: transparent;
                    border-color: var(--joy-color-neutral-0);

                    &:hover {
                        border-color: var(--joy-color-neutral-0);
                        background-color: var(--joy-color-neutral-0);
                        color: var(--joy-color-primary-50);
                    }
                }
            }

            @include mq.screen_xs(min) {
                --hero-header-height: 500px;
            }

            @include mq.screen_sm(min) {
                --hero-header-height: 550px;
                --hero-polygon-height-variation: 50px;
                --hero-polygon-background-size: 350px auto;

                &__title {
                    h1 {
                        text-indent: 0.85em hanging;
                    }
                }

                &__polygon {
                    padding-bottom: 0;
                }

                &__description {
                    font-size: var(--joy-font-size-primary-800);
                    line-height: var(--joy-line-height-large);
                    font-weight: 400;
                    max-width: 90%;
                    padding-bottom: 70px;
                }
            }

            @include mq.screen_lg(min) {
                --hero-header-height: 590px;
                --hero-polygon-height-variation: 80px;
                --hero-polygon-background-size: 630px auto;
                --hero-background-position: calc(100% + 24px) calc(100% + 10px);
                --hero-title-font-size: 85px;
                --hero-subtitle-font-size: 52px;

                .slice-content {
                    padding-top: 90px;
                }
            }
        }
    }
</style>
